import { css } from 'lit';

export const styles = css`
:host {
  display: block;
  
  background: var(--sl-color-neutral-50);
  min-height: 100vh;
}

.dashboard-container {
  display: flex;
  height: 100%;
}

/* Sidebar */
.sidebar {
  width: 250px;
  background: white;
  border-right: 1px solid var(--sl-color-neutral-200);
  padding: 1rem;
  padding-top: 1.5rem;
  transition: width 0.3s ease, padding 0.3s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.sidebar.collapsed {
  width: 70px;
  padding: 1rem 0.5rem;
  padding-top: 1.5rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.25rem;
  border-radius: var(--sl-border-radius-medium);
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
  position: relative;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 0.75rem 0.5rem;
}

.sidebar.collapsed .nav-item span {
  display: none;
}

.nav-item:hover {
  background: var(--sl-color-neutral-100);
}

.nav-item.active {
  background: var(--sl-color-primary-50);
  color: var(--sl-color-primary-700);
}

.nav-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: var(--sl-color-neutral-400);
}

.nav-item.disabled:hover {
  background: transparent;
}

.nav-item.disabled.active {
  background: transparent;
  color: var(--sl-color-neutral-400);
}

/* Main Content */
.main-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

/* Overview Cards */
.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.overview-card {
  background: white;
  border-radius: var(--sl-border-radius-medium);
  padding: 1.25rem 1.5rem;
  box-shadow: var(--sl-shadow-small);
  border: 1px solid var(--sl-color-neutral-200);
  text-align: center;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.overview-card:hover {
  box-shadow: var(--sl-shadow-medium);
  transform: translateY(-2px);
}

.overview-card h3 {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--sl-color-neutral-600);
  margin-bottom: 0.3rem;
}

.overview-card .value {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--sl-color-neutral-900);
}

/* Data Widgets */
.data-widgets {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.widget {
  background: white;
  border-radius: var(--sl-border-radius-medium);
  padding: 1.5rem;
  box-shadow: var(--sl-shadow-small);
  border: 1px solid var(--sl-color-neutral-200);
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
}

.widget:hover {
  box-shadow: var(--sl-shadow-medium);
  transform: translateY(-2px);
  transition: all 0.2s ease;
}

.widget h3 {
  margin: 0 0 1rem 0;
  color: var(--sl-color-neutral-900);
  font-size: 1.125rem;
  font-weight: 600;
}

/* Chart Container */
.chart-container {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-grow: 1;
  justify-content: center;
}

.donut-chart {
  width: 150px;
  height: 150px;
  position: relative;
  flex-shrink: 0;
}

.chart-legend {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

/* Table Styles - for Recent Downtime */
.data-table-container {
  overflow-x: auto;
  flex-grow: 1;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 300px;
}

.data-table th,
.data-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--sl-color-neutral-200);
}

.data-table tbody tr:hover {
  background: var(--sl-color-neutral-100);
}

.data-table th {
  font-weight: 600;
  color: var(--sl-color-neutral-600);
  font-size: 0.875rem;
}

.data-table td {
  color: var(--sl-color-neutral-900);
}

/* PM List */
.pm-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-grow: 1;
}

.pm-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed var(--sl-color-neutral-100);
}

.pm-item:last-child {
  border-bottom: none;
}

.pm-info strong {
  display: block;
  font-size: 0.95rem;
  color: var(--sl-color-neutral-900);
}

.pm-info span {
  font-size: 0.8rem;
  color: var(--sl-color-neutral-600);
}

.pm-assigned {
  font-size: 0.85rem;
  color: var(--sl-color-neutral-700);
}

/* Incidents List */
.incidents-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-grow: 1;
}

.incident-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed var(--sl-color-neutral-100);
}

.incident-item:last-child {
  border-bottom: none;
}

.incident-info strong {
  display: block;
  font-size: 0.95rem;
  color: var(--sl-color-neutral-900);
}

.incident-info span {
  font-size: 0.8rem;
  color: var(--sl-color-neutral-600);
}

/* Loading */
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* No Data Message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--sl-color-neutral-500);
  text-align: center;
  flex-grow: 1;
}

.no-data-message sl-icon {
  margin-bottom: 0.5rem;
  opacity: 0.6;
}

.no-data-message p {
  margin: 0;
  font-size: 0.875rem;
}

/* Downtime List */
.downtime-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Responsive */
@media (max-width: 1200px) {
  .data-widgets {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid var(--sl-color-neutral-200);
    padding: 1rem;
    height: auto;
  }

  .sidebar.collapsed {
    width: 100%;
    padding: 1rem;
  }

  .sidebar .nav-item {
    justify-content: flex-start;
  }

  .sidebar.collapsed .nav-item span {
    display: inline;
  }
  
  .overview-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .data-widgets {
    grid-template-columns: 1fr;
  }
  
  .chart-container {
    flex-direction: column;
  }
}

/* Dashboard Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.dashboard-header .actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.last-updated {
  font-size: 0.75rem;
  color: var(--sl-color-neutral-500);
}

/* Category Grid */
.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}
.category-card {
  background: white;
  border-radius: var(--sl-border-radius-medium);
  padding: 1.5rem;
  box-shadow: var(--sl-shadow-small);
  border: 1px solid var(--sl-color-neutral-200);
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}
.category-card:hover {
  box-shadow: var(--sl-shadow-medium);
  transform: translateY(-2px);
}
.category-card sl-icon {
  font-size: 2rem;
  color: var(--sl-color-primary-600);
}
.category-card .info h3 {
  margin: 0;
  font-size: 1rem;
}
.category-card .info p {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--sl-color-neutral-900);
}
.category-card.expanded {
  border-color: var(--sl-color-primary-500);
  background: var(--sl-color-primary-50);
}
.expanded-widget-content {
  margin-top: 2rem;
  background: white;
  border-radius: var(--sl-border-radius-medium);
  box-shadow: var(--sl-shadow-medium);
  border: 1px solid var(--sl-color-neutral-200);
  overflow: hidden;
}
`;
